/**
 * Composite Icon Component
 *
 * Creates layered icons with a base icon and a badge icon overlay.
 * Used for social engineering icons showing "User via [Medium]" patterns.
 *
 * @example
 * ```tsx
 * // User with email badge
 * <CompositeIcon
 *   baseIcon="lucide:User"
 *   badgeIcon="lucide:Mail"
 * />
 *
 * // User with SMS badge (custom sizes and position)
 * <CompositeIcon
 *   baseIcon="lucide:User"
 *   badgeIcon="lucide:MessageSquare"
 *   baseSize={32}
 *   badgeSize={14}
 *   badgePosition="top-right"
 * />
 * ```
 */

import { AttackGraphIcon } from '@/sections/attacks/attackPaths/components/icons/AttackGraphIcon';

// ============================================================================
// Props Interface
// ============================================================================

export interface CompositeIconProps {
  /** Base icon identifier (e.g., 'lucide:User') */
  baseIcon: string;
  /** Badge icon identifier (e.g., 'lucide:Mail') */
  badgeIcon: string;
  /** Main icon size in pixels (default: 24) */
  baseSize?: number;
  /** Badge size in pixels (default: 10) */
  badgeSize?: number;
  /** Badge position (default: 'bottom-right') */
  badgePosition?: 'bottom-right' | 'top-right' | 'bottom-left' | 'top-left';
  /** CSS class name for styling */
  className?: string;
}

// ============================================================================
// Helper Function
// ============================================================================

/**
 * Get Tailwind classes for badge positioning based on badge position prop.
 */
function getBadgePositionClasses(
  position: CompositeIconProps['badgePosition']
): string {
  switch (position) {
    case 'top-right':
      return '-top-1 -right-1';
    case 'bottom-left':
      return '-bottom-1 -left-1';
    case 'top-left':
      return '-top-1 -left-1';
    case 'bottom-right':
    default:
      return '-bottom-1 -right-1';
  }
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Composite Icon Component.
 *
 * Renders a base icon with a smaller badge icon positioned in a corner.
 * The badge has a circular background for visibility.
 */
export function CompositeIcon({
  baseIcon,
  badgeIcon,
  baseSize = 24,
  badgeSize = 10,
  badgePosition = 'bottom-right',
  className,
}: CompositeIconProps) {
  const badgePositionClasses = getBadgePositionClasses(badgePosition);

  return (
    <div
      className={`relative inline-flex ${className || ''}`}
      style={{ width: baseSize, height: baseSize }}
    >
      {/* Base Icon */}
      <AttackGraphIcon
        icon={baseIcon}
        size={baseSize}
        aria-label={`${baseIcon} icon`}
      />

      {/* Badge Icon Container */}
      <div
        className={`absolute ${badgePositionClasses} flex items-center justify-center`}
        style={{
          width: badgeSize + 4,
          height: badgeSize + 4,
        }}
      >
        {/* Badge Background Circle */}
        <div
          className="bg-chariot-background-secondary absolute inset-0 rounded-full"
          style={{
            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.1)',
          }}
        />

        {/* Badge Icon */}
        <div className="relative z-10">
          <AttackGraphIcon
            icon={badgeIcon}
            size={badgeSize}
            className="text-chariot-text-primary"
            aria-label={`${badgeIcon} badge`}
          />
        </div>
      </div>
    </div>
  );
}
